import { createSlice } from '@reduxjs/toolkit';
import { isSubscriptionProduct } from '../lib/paymentPricing.js';

const initialState = {
  // catalog
  catalogStatus: 'idle',
  products: [],
  recommendedProducts: [],
  catalogError: null,

  // detail
  detailStatus: 'idle',
  currentProductId: null,
  productDetail: null,
  detailError: null,

  // checkout inputs
  couponCode: '',
  couponStatus: 'idle',
  couponError: null,
  discountedPrice: null,
  isSubscription: false,

  // checkout / gateway
  checkoutStatus: 'idle',
  gatewayPayload: null,
  checkoutError: null,

  // post-payment (after gateway return)
  paymentStatus: 'idle',
};

const slice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    libraryRequested(state) {
      state.catalogStatus = 'loading';
      state.catalogError = null;
    },
    catalogReady(state, action) {
      state.catalogStatus = 'ready';
      state.products = action.payload.products ?? [];
      state.recommendedProducts = action.payload.recommended ?? [];
    },
    catalogFailed(state, action) {
      state.catalogStatus = 'failed';
      state.catalogError = action.payload ?? 'Failed to load catalog';
    },

    productDetailRequested(state, action) {
      state.detailStatus = 'loading';
      state.currentProductId = action.payload;
      state.productDetail = null;
      state.detailError = null;
      // Reset checkout inputs when navigating to a new product.
      state.couponCode = '';
      state.couponStatus = 'idle';
      state.couponError = null;
      state.discountedPrice = null;
      state.isSubscription = false;
      state.checkoutStatus = 'idle';
      state.gatewayPayload = null;
      state.checkoutError = null;
    },
    productDetailReady(state, action) {
      state.detailStatus = 'ready';
      state.productDetail = action.payload;
      state.isSubscription = action.payload?.android_billing?.type === 'subscription'
        || isSubscriptionProduct(action.payload);
    },
    productDetailNotFound(state) {
      state.detailStatus = 'not-found';
      state.productDetail = null;
    },
    productDetailFailed(state, action) {
      state.detailStatus = 'failed';
      state.detailError = action.payload ?? 'Failed to load product';
    },

    couponDraftChanged(state, action) {
      state.couponCode = action.payload;
      // Editing the code clears any applied/rejected state so the user has to Apply again.
      state.couponStatus = 'idle';
      state.couponError = null;
      state.discountedPrice = null;
    },
    couponSubmitted(state) {
      state.couponStatus = 'validating';
      state.couponError = null;
    },
    couponApplied(state, action) {
      state.couponStatus = 'applied';
      state.discountedPrice = action.payload.discountedPrice ?? null;
    },
    couponRejected(state, action) {
      state.couponStatus = 'invalid';
      state.couponError = action.payload ?? 'Invalid coupon';
      state.discountedPrice = null;
    },

    // Intermediate action dispatched by the UI. The saga listens to this, reads
    // couponStatus BEFORE the reducer clears it, optionally re-validates, then
    // dispatches subscriptionToggled to commit the actual state change.
    subscriptionToggleRequested(state, action) {
      // No state change here — saga handles ordering.
      void action;
    },
    subscriptionToggled(state, action) {
      state.isSubscription = !!action.payload;
      // Subscription change invalidates any applied coupon — different price tier.
      if (state.couponStatus === 'applied') {
        state.couponStatus = 'idle';
        state.discountedPrice = null;
      }
    },

    paymentStarted(state) {
      state.checkoutStatus = 'pending';
      state.checkoutError = null;
      state.gatewayPayload = null;
    },
    gatewayReady(state, action) {
      state.checkoutStatus = 'gateway-ready';
      state.gatewayPayload = action.payload;
    },
    paymentFailed(state, action) {
      state.checkoutStatus = 'failed';
      state.checkoutError = action.payload ?? 'Payment could not be started';
    },
    androidSubscriptionPurchaseSucceeded(state) {
      state.checkoutStatus = 'idle';
      state.checkoutError = null;
      state.gatewayPayload = null;
    },
    androidSubscriptionPurchasePending(state) {
      state.checkoutStatus = 'idle';
      state.checkoutError = null;
      state.gatewayPayload = null;
    },
    androidSubscriptionPurchaseCancelled(state) {
      state.checkoutStatus = 'idle';
      state.checkoutError = null;
      state.gatewayPayload = null;
    },
    paymentHandoffAbandoned(state) {
      state.checkoutStatus = 'idle';
      state.gatewayPayload = null;
      state.checkoutError = null;
    },

    paymentReturnReceived(state) {
      state.paymentStatus = 'processing';
      // Payload is the raw query string (passed straight through to log_payment).
      // Saga reads it from the action; nothing to store in state.
    },
    paymentSucceeded(state) {
      state.paymentStatus = 'success';
    },
    paymentAlreadyProcessed(state) {
      state.paymentStatus = 'already-processed';
    },
    paymentRejected(state) {
      state.paymentStatus = 'failed';
    },

    librarySliceReset() {
      return initialState;
    },
  },
});

export const {
  libraryRequested,
  catalogReady,
  catalogFailed,
  productDetailRequested,
  productDetailReady,
  productDetailNotFound,
  productDetailFailed,
  couponDraftChanged,
  couponSubmitted,
  couponApplied,
  couponRejected,
  subscriptionToggleRequested,
  subscriptionToggled,
  paymentStarted,
  gatewayReady,
  paymentFailed,
  androidSubscriptionPurchaseSucceeded,
  androidSubscriptionPurchasePending,
  androidSubscriptionPurchaseCancelled,
  paymentHandoffAbandoned,
  paymentReturnReceived,
  paymentSucceeded,
  paymentAlreadyProcessed,
  paymentRejected,
  librarySliceReset,
} = slice.actions;
export const libraryReducer = slice.reducer;
